import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { Loader2 } from "lucide-react";

import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Overview from "@/pages/Overview";
import FinanceDashboard from "@/pages/finance/FinanceDashboard";
import Revenue from "@/pages/finance/Revenue";
import Expenses from "@/pages/finance/Expenses";
import Budget from "@/pages/finance/Budget";
import CashFlow from "@/pages/finance/CashFlow";
import Labour from "@/pages/finance/Labour";
import Gst from "@/pages/finance/Gst";
import CustomerProfitability from "@/pages/finance/CustomerProfitability";
import ProductProfitability from "@/pages/finance/ProductProfitability";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Usage from "@/pages/Usage";
import Integrations from "@/pages/Integrations";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import BankingSettings from "@/pages/BankingSettings";
import CommercialPackages from "@/pages/CommercialPackages";
import MolarooFinance from "@/pages/MolarooFinance";
import Payments from "@/pages/finance/Payments";
import Help from "@/pages/Help";
import LegalDashboard from "@/pages/legal/LegalDashboard";
import LegalTemplates from "@/pages/legal/Templates";
import TemplateDetail from "@/pages/legal/TemplateDetail";
import LegalReview from "@/pages/legal/LegalReview";
import LegalAlerts from "@/pages/legal/LegalAlerts";
import LegalContracts from "@/pages/legal/Contracts";
import ContractDetail from "@/pages/legal/ContractDetail";
import LegalRegister from "@/pages/legal/LegalRegister";
import LegalCustomers from "@/pages/legal/LegalCustomers";
import LegalGenerator from "@/pages/legal/Generator";
import ExecutedDocuments from "@/pages/legal/ExecutedDocuments";
import LegalSettings from "@/pages/legal/LegalSettings";
import ComingSoon from "@/pages/legal/ComingSoon";
import InvoicesDashboard from "@/pages/invoices/InvoicesDashboard";
import NewInvoice from "@/pages/invoices/NewInvoice";
import InvoiceDetail from "@/pages/invoices/InvoiceDetail";
import InvoiceSettings from "@/pages/invoices/InvoiceSettings";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B1120]">
        <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<Overview />} />
            <Route path="/finance" element={<FinanceDashboard />} />
            <Route path="/finance/revenue" element={<Revenue />} />
            <Route path="/finance/expenses" element={<Expenses />} />
            <Route path="/finance/budget" element={<Budget />} />
            <Route path="/finance/cash-flow" element={<CashFlow />} />
            <Route path="/finance/labour" element={<Labour />} />
            <Route path="/finance/gst" element={<Gst />} />
            <Route path="/finance/customer-profitability" element={<CustomerProfitability />} />
            <Route path="/finance/product-profitability" element={<ProductProfitability />} />
            <Route path="/finance/payments" element={<Payments />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/customers/:id" element={<CustomerDetail />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:id" element={<ProductDetail />} />
            <Route path="/usage" element={<Usage />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/settings/banking" element={<BankingSettings />} />
            <Route path="/commercial/packages" element={<CommercialPackages />} />
            <Route path="/products/molaroo/finance" element={<MolarooFinance />} />
            <Route path="/help" element={<Help />} />
            <Route path="/invoices" element={<InvoicesDashboard />} />
            <Route path="/invoices/new" element={<NewInvoice />} />
            <Route path="/invoices/settings" element={<InvoiceSettings />} />
            <Route path="/invoices/:id" element={<InvoiceDetail />} />
            <Route path="/legal" element={<LegalDashboard />} />
            <Route path="/legal/templates" element={<LegalTemplates />} />
            <Route path="/legal/templates/:id" element={<TemplateDetail />} />
            <Route path="/legal/review" element={<LegalReview />} />
            <Route path="/legal/alerts" element={<LegalAlerts />} />
            <Route path="/legal/contracts" element={<LegalContracts />} />
            <Route path="/legal/contracts/:id" element={<ContractDetail />} />
            <Route path="/legal/customers" element={<LegalCustomers />} />
            <Route path="/legal/generator" element={<LegalGenerator />} />
            <Route path="/legal/register" element={<LegalRegister />} />
            <Route path="/legal/executed" element={<ExecutedDocuments />} />
            <Route path="/legal/settings" element={<LegalSettings />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
